export const POINTS = { EXACT: 10, WINNER_DIFF: 7, WINNER: 4, GOALS: 2, MISS: 0 };

export const POINT_STYLES = {
  10: { label: "scoring.label.exact",       emoji: "🎯", color: "#16a34a" },
  7:  { label: "scoring.label.winnerDiff",  emoji: "🥈", color: "#0ea5e9" },
  4:  { label: "scoring.label.winner",      emoji: "✅", color: "#6366f1" },
  2:  { label: "scoring.label.goals",       emoji: "🎲", color: "#f59e0b" },
  0:  { label: "scoring.label.miss",        emoji: "❌", color: "#9ca3af" },
};

function standardPoints(aH, aA, pH, pA) {
  if (aH === pH && aA === pA) return POINTS.EXACT;
  const aSign = Math.sign(aH - aA);
  const pSign = Math.sign(pH - pA);
  if (aSign === pSign) {
    if (aH - aA === pH - pA) return POINTS.WINNER_DIFF;
    return POINTS.WINNER;
  }
  if (aH + aA === pH + pA) return POINTS.GOALS;
  return POINTS.MISS;
}

export function calculatePoints(input) {
  const {
    sport, scenario,
    actualHome, actualAway, predHome, predAway,
    actualOvertime, predOvertime,
    actualAdvancing, predAdvancing,
  } = input;

  if (sport === "FOOTBALL" && scenario === "STANDARD") {
    return standardPoints(actualHome, actualAway, predHome, predAway);
  }

  if (sport === "FOOTBALL") {
    if (actualOvertime && predOvertime) {
      const correctAdv = actualAdvancing && actualAdvancing === predAdvancing;
      const exact = actualHome === predHome && actualAway === predAway;
      if (exact && correctAdv) return POINTS.EXACT;       // 10 – exact 90-min score + correct advancing
      if (exact) return POINTS.WINNER_DIFF;               // 7  – exact 90-min score, wrong advancing
      if (correctAdv) return POINTS.WINNER;               // 4  – correct advancing, wrong 90-min score
      return POINTS.GOALS;                                // 2  – only predicted that overtime happens
    }
    if (!actualOvertime && !predOvertime) {
      return standardPoints(actualHome, actualAway, predHome, predAway);
    }
    // Overtime prediction does not match reality (predicted OT but none, or OT but not predicted) → 0.
    return POINTS.MISS;
  }

  // HOCKEY
  if (actualOvertime && predOvertime) {
    if (actualHome === predHome && actualAway === predAway) return POINTS.EXACT;
    const correctWinner = Math.sign(actualHome - actualAway) === Math.sign(predHome - predAway);
    const actualReg = Math.min(actualHome, actualAway);
    const predReg = Math.min(predHome, predAway);
    if (correctWinner || actualReg === predReg) return POINTS.WINNER_DIFF;
    return POINTS.WINNER;
  }
  if (!actualOvertime && !predOvertime) {
    return standardPoints(actualHome, actualAway, predHome, predAway);
  }
  if (actualHome + actualAway === predHome + predAway) return POINTS.GOALS;
  return POINTS.MISS;
}

/**
 * Vrací krátký textový popis, proč uživatel dostal daný počet bodů.
 * Vrací i18n klíč + parametry — překlad se pak skládá v UI vrstvě.
 */
export function getPointsReason(input, points) {
  const {
    sport, scenario,
    predHome, predAway, actualHome, actualAway,
    actualOvertime, predOvertime,
  } = input;
  const diff = predHome - predAway;
  const total = predHome + predAway;
  const winnerSide = Math.sign(actualHome - actualAway);

  // Football knockout / second leg that actually went to overtime is scored on the
  // 90-min score + advancing team, so the generic "winner / goal difference" wording
  // would be misleading — use dedicated explanations instead.
  if (sport === "FOOTBALL" && scenario !== "STANDARD") {
    if (actualOvertime && predOvertime) {
      if (points === POINTS.EXACT)       return { key: "scoring.reason.koExactAdvancing", params: {} };
      if (points === POINTS.WINNER_DIFF) return { key: "scoring.reason.koExactWrongAdvancing", params: {} };
      if (points === POINTS.WINNER)      return { key: "scoring.reason.koAdvancingWrongScore", params: {} };
      return { key: "scoring.reason.koOvertimeOnly", params: {} };
    }
    if (actualOvertime !== predOvertime) {
      return { key: "scoring.reason.koOvertimeMismatch", params: {} };
    }
  }

  if (points === POINTS.EXACT) {
    return { key: "scoring.reason.exact", params: { home: actualHome, away: actualAway } };
  }
  if (points === POINTS.WINNER_DIFF) {
    const diffSigned = diff > 0 ? `+${diff}` : `${diff}`;
    return { key: "scoring.reason.winnerDiff", params: { diff: diffSigned } };
  }
  if (points === POINTS.WINNER) {
    const winnerKey = winnerSide > 0
      ? "scoring.reason.winnerHome"
      : winnerSide < 0
        ? "scoring.reason.winnerAway"
        : "scoring.reason.winnerDraw";
    return { key: "scoring.reason.winner", params: { winner: winnerKey } };
  }
  if (points === POINTS.GOALS) {
    return { key: "scoring.reason.goals", params: { total } };
  }
  return { key: "scoring.reason.miss", params: {} };
}
